import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { TimeFormatType } from '@demind-inc/core';
import NearbyPlaceRow from './NearbyPlaceRow';
import GymWorkoutPlanContent from './WorkoutPlan/GymWorkoutPlanContent';
import CardioWorkoutPlanContent from './WorkoutPlan/CardioWorkoutPlanContent';
import OtherWorkoutPlanContent from './WorkoutPlan/OtherWorkoutPlanContent';
import { AssistantChatSuggestedBlockCTA } from '../AssistantChatSuggestedBlockCTA';
import type { WorkoutPlanPayload } from '../../../data-access/api/science/openapi/api';
import './richContext.scss';
import { useTranslation } from 'react-i18next';
import { useGeneralSettings } from '../../../hooks';
import { formatTimeFromDayjs } from '../../../helpers/time.helper';

export type RichContextCardCTA = {
  title: string;
  onClick: () => void;
  type?: 'calendar' | 'task';
};

interface WorkoutPlanCardProps {
  p: WorkoutPlanPayload;
  enrichmentPending?: boolean;
  ctas?: RichContextCardCTA[];
}

const formatWorkoutScheduledTime = (value: unknown, timeFormat: TimeFormatType): string | null => {
  if (typeof value !== 'string' || !value.trim()) return null;
  const trimmed = value.trim();

  const hhmmMatch = trimmed.match(/^(\d{1,2}):(\d{2})/);
  if (hhmmMatch) {
    const normalized = `${hhmmMatch[1].padStart(2, '0')}:${hhmmMatch[2]}`;
    return formatTimeFromDayjs(
      dayjs(`${dayjs().format('YYYY-MM-DD')}T${normalized}:00`),
      timeFormat,
      true
    );
  }

  const parsed = dayjs(trimmed);
  if (parsed.isValid()) {
    return formatTimeFromDayjs(parsed, timeFormat, true);
  }

  return null;
};

const WorkoutPlanCard: React.FC<WorkoutPlanCardProps> = ({ p, enrichmentPending, ctas }) => {
  const { t } = useTranslation();
  const {
    generalSettings: { timeFormat },
  } = useGeneralSettings();
  const exercises = Array.isArray(p.exercises) ? p.exercises : [];
  const nearbyPlaces = Array.isArray(p.nearby_places) ? p.nearby_places : [];
  const showNearbyPlaces = nearbyPlaces.length > 0;
  const showNearbyLoading = !showNearbyPlaces && enrichmentPending;
  const showWorkoutLoading = Boolean(enrichmentPending) && exercises.length === 0;
  const safeCtas = Array.isArray(ctas) ? ctas.filter((c) => !!c?.title && !!c?.onClick) : [];

  const workoutType = typeof p.workout_type === 'string' ? p.workout_type : undefined;
  const isGym = workoutType === 'gym';
  const isCardio =
    workoutType === 'running' ||
    workoutType === 'walking' ||
    workoutType === 'cycling' ||
    workoutType === 'swimming' ||
    workoutType === 'rowing' ||
    workoutType === 'hiking';
  const suggestedTimeLabel = useMemo(() => {
    const scheduledStart = formatWorkoutScheduledTime(
      (p as any).scheduled_start_time ?? (p as any).start_time,
      timeFormat
    );
    const scheduledEnd = formatWorkoutScheduledTime(
      (p as any).scheduled_end_time ?? (p as any).end_time,
      timeFormat
    );

    if (scheduledStart && scheduledEnd) {
      return t('task.richContext.suggestedTimeRange', {
        start: scheduledStart,
        end: scheduledEnd,
      });
    }
    if (scheduledStart) {
      return t('task.richContext.suggestedTime', { time: scheduledStart });
    }
    return null;
  }, [p, t, timeFormat]);
  const durationLabel =
    typeof p.duration_min === 'number' ? `${p.duration_min} min` : null;
  const metaLine = [suggestedTimeLabel, durationLabel].filter(Boolean).join(' · ');

  return (
    <div className="rc-content-body">
      {!!metaLine && <span className="rc-content-meta">{metaLine}</span>}

      {showWorkoutLoading ? (
        <div className="rc-skeleton-group">
          <div className="rc-skeleton-row" />
          <div className="rc-skeleton-row" />
          <div className="rc-skeleton-row rc-skeleton-row--short" />
        </div>
      ) : isGym ? (
        <GymWorkoutPlanContent exercises={exercises} />
      ) : isCardio ? (
        <CardioWorkoutPlanContent exercises={exercises} />
      ) : (
        <OtherWorkoutPlanContent exercises={exercises} />
      )}

      {showNearbyPlaces && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span className="rc-content-meta">{t('task.richContext.nearbySpots')}</span>
          <div className="rc-nearby-list">
            {nearbyPlaces.map((place, i) => (
              <NearbyPlaceRow key={i} place={place} />
            ))}
          </div>
        </div>
      )}

      {showNearbyLoading && (
        <div className="rc-skeleton-group">
          <div className="rc-skeleton-row" />
          <div className="rc-skeleton-row rc-skeleton-row--short" />
        </div>
      )}

      {safeCtas.map((cta, idx) => (
        <AssistantChatSuggestedBlockCTA
          key={`${cta.title}_${idx}`}
          title={cta.title}
          onClick={cta.onClick}
          type={cta.type ?? 'calendar'}
        />
      ))}
    </div>
  );
};

export default WorkoutPlanCard;
